/**
 * Theme store — two independent hues drive the whole token system:
 *   accent : the bright / active colour (highlights, active states, sliders).
 *   tint   : the dark BACKGROUND tone (surfaces, panels, ambient glow,
 *            waveform). Decoupled so e.g. a magenta accent can sit on
 *            near-neutral indigo surfaces.
 *
 * Each is either a named preset (mapped to a hue) or 'custom' (a hex whose
 * OKLCH hue is used). Both are written as inline `--accent-h` / `--tint-h`
 * on <html> and persisted to localStorage.
 */

import { create } from 'zustand';

export type ThemeChoice = 'magenta' | 'cyan' | 'indigo' | 'sunset' | 'yellow' | 'purple' | 'white' | 'custom';

/** Interactive background visual (particles reacting to the map or the music). */
export type BgVisual = 'none' | 'aurora' | 'embers' | 'ripple';
/** What the interactive visual reacts to. */
export type BgVisualSource = 'map' | 'music';

/** Preset palette (shared by both the accent and tint pickers). */
export const THEME_PRESETS: { id: Exclude<ThemeChoice, 'custom'>; label: string; hue: number; c?: number }[] = [
  { id: 'magenta', label: 'Magenta', hue: 322 },
  { id: 'purple',  label: 'Purple',  hue: 300 },
  { id: 'indigo',  label: 'Indigo',  hue: 268 },
  { id: 'cyan',    label: 'Cyan',    hue: 195 },
  { id: 'yellow',  label: 'Yellow',  hue: 100 },
  { id: 'sunset',  label: 'Sunset',  hue: 32 },
  { id: 'white',   label: 'White',   hue: 322, c: 0 },   // desaturated → white / grayscale
];

/** Chroma multiplier for a choice (1 = full colour; the White preset → 0). */
function presetChroma(choice: ThemeChoice): number {
  if (choice === 'custom') return 1;
  return THEME_PRESETS.find(p => p.id === choice)?.c ?? 1;
}
/** Back-compat alias (older imports). */
export const THEME_ACCENTS = THEME_PRESETS;

const K = {
  accent: 'synapse:theme:accent',
  accentHex: 'synapse:theme:customHex',      // legacy key name kept for accent
  editorAccents: 'synapse:theme:editorAccents',
  tint: 'synapse:theme:tint',
  tintHex: 'synapse:theme:tintHex',
  bg: 'synapse:theme:bg',
  bgHex: 'synapse:theme:bgHex',
  bgBrightness: 'synapse:theme:bgBrightness',
  bgVisual: 'synapse:theme:bgVisual',
  bgVisualSource: 'synapse:theme:bgVisualSource',
  tooltipsEnabled: 'synapse:theme:tooltipsEnabled',
  accentL: 'synapse:theme:accentL',
  accentStructL: 'synapse:theme:accentStructL',
  textL: 'synapse:theme:textL',
  tintL: 'synapse:theme:tintL',
};
const DEFAULT_HEX = '#D946EF';
const DEFAULT_BG_HEX = '#7C3AED';
const DEFAULT_BG_BRIGHTNESS = 1;

/** Per-editor accent. Each editor (and the dashboard) keeps its OWN accent
 *  colour; the active editor's accent is what drives --accent-h. Defaults match
 *  the dashboard card colours so each editor starts visually distinct. */
export type AccentSlot = { choice: ThemeChoice; hex: string };
const DEFAULT_EDITOR_ACCENTS: Record<string, AccentSlot> = {
  dashboard:      { choice: 'custom', hex: DEFAULT_HEX },
  'synth-rider':  { choice: 'custom', hex: '#FF2E88' },
  console:        { choice: 'custom', hex: '#1CC5F0' },
  flow:           { choice: 'custom', hex: '#A22BF0' },
  roobloops:      { choice: 'custom', hex: '#FFD84D' },
};

/** Convert a #rrggbb colour to its OKLCH hue in degrees (0–360). */
export function hexToOklchHue(hex: string): number {
  const m = hex.replace('#', '');
  if (m.length < 6) return 322;
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const R = lin(r), G = lin(g), B = lin(b);
  const l = 0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B;
  const mm = 0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B;
  const s = 0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B;
  const l_ = Math.cbrt(l), m_ = Math.cbrt(mm), s_ = Math.cbrt(s);
  const A = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const Bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
  let h = (Math.atan2(Bb, A) * 180) / Math.PI;
  if (h < 0) h += 360;
  return Math.round(h * 10) / 10;
}

function presetHue(id: ThemeChoice): number {
  return THEME_PRESETS.find(p => p.id === id)?.hue ?? 322;
}
/** Resolve a choice + its custom hex to a concrete hue. */
function hueFor(choice: ThemeChoice, hex: string): number {
  return choice === 'custom' ? hexToOklchHue(hex) : presetHue(choice);
}

function loadChoice(key: string, fallback: ThemeChoice = 'magenta'): ThemeChoice {
  try {
    const v = localStorage.getItem(key);
    if (v === 'magenta' || v === 'cyan' || v === 'indigo' || v === 'sunset' || v === 'yellow' || v === 'purple' || v === 'white' || v === 'custom') return v;
  } catch { /* ignore */ }
  return fallback;
}
function loadHex(key: string, fallback: string = DEFAULT_HEX): string {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}
function loadNum(key: string, fallback: number): number {
  try { const v = parseFloat(localStorage.getItem(key) || ''); return Number.isFinite(v) ? v : fallback; }
  catch { return fallback; }
}

function loadBgVisual(): BgVisual {
  try {
    const v = localStorage.getItem(K.bgVisual);
    if (v === 'none' || v === 'aurora' || v === 'embers' || v === 'ripple') return v;
  } catch { /* ignore */ }
  return 'none';
}
function loadBgVisualSource(): BgVisualSource {
  try {
    const v = localStorage.getItem(K.bgVisualSource);
    if (v === 'map' || v === 'music') return v;
  } catch { /* ignore */ }
  return 'map';
}

interface ThemeState {
  // accent / accentHex MIRROR the active editor's slot (so the picker UI keeps
  // working unchanged); editorAccents is the per-editor source of truth.
  accent: ThemeChoice; accentHex: string;
  editorAccents: Record<string, AccentSlot>;
  activeEditor: string;            // 'dashboard' | 'flow' | 'synth-rider' | 'console' | …
  tint: ThemeChoice; tintHex: string;
  bg: ThemeChoice; bgHex: string; bgBrightness: number;
  bgVisual: BgVisual; bgVisualSource: BgVisualSource;
  tooltipsEnabled: boolean;
  accentL: number; accentStructL: number; tintL: number; textL: number;
}

/** The accent slot for an editor key (falls back to the dashboard slot). */
function slotFor(s: ThemeState, key: string): AccentSlot {
  return s.editorAccents[key] ?? s.editorAccents.dashboard ?? { choice: s.accent, hex: s.accentHex };
}

/** Resolve an editor's accent to a concrete OKLCH hue (for the dashboard cards). */
export function editorAccentHue(slot: AccentSlot | undefined): number {
  return slot ? hueFor(slot.choice, slot.hex) : presetHue('magenta');
}

function apply(s: ThemeState): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  const acc = slotFor(s, s.activeEditor);
  html.style.setProperty('--accent-h', String(hueFor(acc.choice, acc.hex)));
  html.style.setProperty('--tint-h', String(hueFor(s.tint, s.tintHex)));
  // Chroma multipliers — the "White" preset desaturates accent / tint to grey.
  html.style.setProperty('--accent-c', String(presetChroma(acc.choice)));
  html.style.setProperty('--tint-c', String(presetChroma(s.tint)));
  // Lightness multipliers (1 = default) — darken/lighten the accent & tint ramps.
  html.style.setProperty('--accent-l', String(s.accentL));
  html.style.setProperty('--accent-struct-l', String(s.accentStructL));
  html.style.setProperty('--tint-l', String(s.tintL));
  html.style.setProperty('--text-l', String(s.textL));
  // Background = the 3D timeline backdrop: its own hue + a brightness multiplier
  // + chroma (the "White" bg preset desaturates it to neutral grey).
  html.style.setProperty('--bg3d-h', String(hueFor(s.bg, s.bgHex)));
  html.style.setProperty('--bg3d-b', String(s.bgBrightness));
  html.style.setProperty('--bg3d-c', String(presetChroma(s.bg)));
}

function loadEditorAccents(): Record<string, AccentSlot> {
  try {
    const raw = localStorage.getItem(K.editorAccents);
    if (raw) return { ...DEFAULT_EDITOR_ACCENTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  // Seed the dashboard slot from the legacy global accent if present.
  return { ...DEFAULT_EDITOR_ACCENTS, dashboard: { choice: loadChoice(K.accent), hex: loadHex(K.accentHex) } };
}

function loadState(): ThemeState {
  const editorAccents = loadEditorAccents();
  const dash = editorAccents.dashboard;
  return {
    accent: dash.choice,
    accentHex: dash.hex,
    editorAccents,
    activeEditor: 'dashboard',
    tint: loadChoice(K.tint),
    tintHex: loadHex(K.tintHex),
    bg: loadChoice(K.bg, 'purple'),
    bgHex: loadHex(K.bgHex, DEFAULT_BG_HEX),
    bgBrightness: loadNum(K.bgBrightness, DEFAULT_BG_BRIGHTNESS),
    bgVisual: loadBgVisual(),
    bgVisualSource: loadBgVisualSource(),
    tooltipsEnabled: (() => { try { return localStorage.getItem(K.tooltipsEnabled) !== '0'; } catch { return true; } })(),
    accentL: loadNum(K.accentL, 1),
    accentStructL: loadNum(K.accentStructL, 1),
    tintL: loadNum(K.tintL, 1),
    textL: loadNum(K.textL, 1),
  };
}

/** Apply the saved theme to <html>. Called once at boot (before React mounts). */
export function initTheme(): void {
  apply(loadState());
}

interface ThemeStore extends ThemeState {
  setAccent: (a: Exclude<ThemeChoice, 'custom'>) => void;
  setAccentHex: (hex: string) => void;
  /** Switch which editor's accent is applied (call on view change). */
  setActiveEditor: (id: string) => void;
  setTint: (t: Exclude<ThemeChoice, 'custom'>) => void;
  setTintHex: (hex: string) => void;
  setBg: (b: Exclude<ThemeChoice, 'custom'>) => void;
  setBgHex: (hex: string) => void;
  setBgBrightness: (v: number) => void;
  setBgVisual: (v: BgVisual) => void;
  setBgVisualSource: (s: BgVisualSource) => void;
  setTooltipsEnabled: (v: boolean) => void;
  setAccentL: (v: number) => void;
  setAccentStructL: (v: number) => void;
  setTextL: (v: number) => void;
  setTintL: (v: number) => void;
}

export const useTheme = create<ThemeStore>((set, get) => ({
  ...loadState(),

  // setAccent / setAccentHex target the ACTIVE editor's slot (or the dashboard
  // slot when on the menu). accent/accentHex mirror it for the picker UI.
  setAccent: (a) => {
    const st = get();
    const key = st.activeEditor;
    const editorAccents = { ...st.editorAccents, [key]: { choice: a, hex: slotFor(st, key).hex } };
    set({ editorAccents, accent: a });
    apply(get());
    try { localStorage.setItem(K.editorAccents, JSON.stringify(editorAccents)); } catch { /* ignore */ }
  },
  setAccentHex: (hex) => {
    const st = get();
    const key = st.activeEditor;
    const editorAccents = { ...st.editorAccents, [key]: { choice: 'custom' as ThemeChoice, hex } };
    set({ editorAccents, accent: 'custom', accentHex: hex });
    apply(get());
    try { localStorage.setItem(K.editorAccents, JSON.stringify(editorAccents)); } catch { /* ignore */ }
  },
  setActiveEditor: (id) => {
    const st = get();
    const slot = slotFor(st, id);
    set({ activeEditor: id, accent: slot.choice, accentHex: slot.hex });
    apply(get());
  },
  setTint: (t) => {
    set({ tint: t });
    apply(get());
    try { localStorage.setItem(K.tint, t); } catch { /* ignore */ }
  },
  setTintHex: (hex) => {
    set({ tint: 'custom', tintHex: hex });
    apply(get());
    try { localStorage.setItem(K.tint, 'custom'); localStorage.setItem(K.tintHex, hex); } catch { /* ignore */ }
  },
  setBg: (b) => {
    set({ bg: b });
    apply(get());
    try { localStorage.setItem(K.bg, b); } catch { /* ignore */ }
  },
  setBgHex: (hex) => {
    set({ bg: 'custom', bgHex: hex });
    apply(get());
    try { localStorage.setItem(K.bg, 'custom'); localStorage.setItem(K.bgHex, hex); } catch { /* ignore */ }
  },
  setBgBrightness: (v) => {
    set({ bgBrightness: v });
    apply(get());
    try { localStorage.setItem(K.bgBrightness, String(v)); } catch { /* ignore */ }
  },
  setBgVisual: (v) => {
    set({ bgVisual: v });
    try { localStorage.setItem(K.bgVisual, v); } catch { /* ignore */ }
  },
  setBgVisualSource: (s) => {
    set({ bgVisualSource: s });
    try { localStorage.setItem(K.bgVisualSource, s); } catch { /* ignore */ }
  },
  setTooltipsEnabled: (v) => {
    set({ tooltipsEnabled: v });
    try { localStorage.setItem(K.tooltipsEnabled, v ? '1' : '0'); } catch { /* ignore */ }
  },
  setAccentL: (v) => {
    set({ accentL: v });
    try { localStorage.setItem(K.accentL, String(v)); } catch { /* ignore */ }
    apply(get());
  },
  setAccentStructL: (v) => {
    set({ accentStructL: v });
    try { localStorage.setItem(K.accentStructL, String(v)); } catch { /* ignore */ }
    apply(get());
  },
  setTextL: (v) => {
    set({ textL: v });
    try { localStorage.setItem(K.textL, String(v)); } catch { /* ignore */ }
    apply(get());
  },
  setTintL: (v) => {
    set({ tintL: v });
    try { localStorage.setItem(K.tintL, String(v)); } catch { /* ignore */ }
    apply(get());
  },
}));
